import React, { useEffect } from "react";
import Header from "../../components/ui/Header";
import HeroSection from "./components/HeroSection";
import ProblemSection from "./components/ProblemSection";
import SolutionSection from "./components/SolutionSection";
import ServicesSection from "./components/ServicesSection";
import PricingSection from "./components/PricingSection";
import TestimonialsSection from "./components/TestimonialsSection";
import PortfolioSection from "./components/PortfolioSection";
import ProcessSection from "./components/ProcessSection";
import ContactSection from "./components/ContactSection";
import FAQSection from "./components/FAQSection";
import ClientLogosSection from "./components/ClientLogosSection";
import FooterSection from "./components/FooterSection";

const LandingPage = () => {
  useEffect(() => {
    // Set page title
    document.title = "FreelanceHub - Your One-Stop Freelance Solution";

    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "Access vetted professionals with transparent pricing, guaranteed quality, and streamlined project management. Transform your business with FreelanceHub."
      );
    } else {
      const meta = document.createElement("meta");
      meta.name = "description";
      meta.content =
        "Access vetted professionals with transparent pricing, guaranteed quality, and streamlined project management. Transform your business with FreelanceHub.";
      document.getElementsByTagName("head")[0].appendChild(meta);
    }

    // Add smooth scrolling and performance optimizations
    const style = document.createElement("style");
    style.textContent = `
      /* Performance optimizations */
      * {
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }
      
      /* Smooth scrolling for all browsers */
      html {
        scroll-behavior: smooth;
      }
      
      /* Reduce motion for accessibility */
      @media (prefers-reduced-motion: reduce) {
        *, *::before, *::after {
          animation-duration: 0.01ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0.01ms !important;
        }
      }
    `;
    document.head.appendChild(style);

    // Add structured data for SEO
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "FreelanceHub",
      description:
        "Professional freelance services with vetted talent and guaranteed results",
      url: window.location.origin,
      logo: `${window.location.origin}/logo.png`,
      contactPoint: {
        "@type": "ContactPoint",
        telephone: "+1-555-123-4567",
        contactType: "customer service",
        email: "hello@freelancehub.com",
      },
      sameAs: [
        "https://facebook.com/freelancehub",
        "https://twitter.com/freelancehub",
        "https://linkedin.com/company/freelancehub",
      ],
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.9",
        reviewCount: "5000",
      },
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(structuredData);
    document.getElementsByTagName("head")[0].appendChild(script);

    // Cleanup function
    return () => {
      // Remove the structured data script when component unmounts
      const scripts = document.querySelectorAll(
        'script[type="application/ld+json"]'
      );
      scripts.forEach((script) => {
        if (script.text.includes("FreelanceHub")) {
          script.remove();
        }
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50/30 to-purple-50/20 relative overflow-hidden">
      {/* Global background effects */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-r from-blue-200/20 to-purple-200/20 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-gradient-to-r from-purple-200/20 to-pink-200/20 rounded-full mix-blend-multiply filter blur-3xl animate-blob animate-blob-delay-2"></div>
        <div className="absolute top-1/2 left-0 w-64 h-64 bg-gradient-to-r from-cyan-200/20 to-blue-200/20 rounded-full mix-blend-multiply filter blur-3xl animate-float-slow"></div>
      </div>

      {/* Header Navigation */}
      <div className="relative z-50">
        <Header />
      </div>

      {/* Page Sections */}
      <div className="relative z-10">
        {/* Hero Section */}
        <HeroSection />

        {/* Problem Section */}
        <ProblemSection />

        {/* Solution Section */}
        <SolutionSection />

        {/* Services Section */}
        <ServicesSection />

        {/* Client Logos Section */}
        <ClientLogosSection />

        {/* Portfolio Section */}
        <PortfolioSection />

        {/* Process Section */}
        <ProcessSection />

        {/* Testimonials Section */}
        <TestimonialsSection />

        {/* Pricing Section */}
        {/* <PricingSection /> */}

        {/* FAQ Section */}
        <FAQSection />

        {/* Contact Section */}
        {/* <ContactSection /> */}

        {/* Footer Section */}
        <FooterSection />
      </div>
    </div>
  );
};

export default LandingPage;
